import { Button } from "@shared/ui/button";
import {
  Archive,
  Copy,
  Download,
  Edit,
  Eye,
  LucideIcon,
  Printer,
} from "lucide-react";
import React from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  useArchiveTemplate,
  useCloneTemplate,
  useDownloadBillTemplate,
  usePrintBillTemplate,
  useViewBillTemplate,
} from "../../hooks";

interface QuickAction {
  label: string;
  icon: LucideIcon;
  onClick: () => void;
  variant?: "default" | "outline" | "destructive";
  className?: string;
  isLoading?: boolean;
}

export const QuickActions: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const cloneTemplate = useCloneTemplate();
  const archiveTemplate = useArchiveTemplate();
  const downloadTemplate = useDownloadBillTemplate();
  const viewBillTemplate = useViewBillTemplate();
  const printBillTemplate = usePrintBillTemplate();

  const handleEditTemplate = () => {
    navigate(`/bx/bill-setup/billtemplate/template-setup/view/null/${id}`);
  };

  const handleCloneTemplate = () => {
    cloneTemplate.mutate(id);
  };

  const handleViewTemplate = async () => {
    try {
      await viewBillTemplate.mutateAsync(id);
    } catch (error) {
      console.error("View failed:", error);
      // showToast('Failed to view the bill', 'error');
    }
  };

  const handleDownloadTemplate = async () => {
    await downloadTemplate.mutateAsync({
      id: id,
      filename: `template-${id}.pdf`,
    });
  };

  const handlePrintTemplate = async () => {
    await printBillTemplate.mutateAsync(id);
  };

  const handleArchiveTemplate = () => {
    archiveTemplate.mutate(id);
  };

  const actions: QuickAction[] = [
    {
      label: "Edit Template",
      icon: Edit,
      onClick: handleEditTemplate,
      variant: "outline",
    },
    {
      label: "Duplicate Template",
      icon: Copy,
      onClick: handleCloneTemplate,
      variant: "outline",
      isLoading: cloneTemplate.isPending,
    },
    {
      label: "View Template",
      icon: Eye,
      onClick: handleViewTemplate,
      variant: "outline",
    },
    {
      label: "Download Template",
      icon: Download,
      onClick: handleDownloadTemplate,
      variant: "outline",
    },
    {
      label: "Print Template",
      icon: Printer,
      onClick: handlePrintTemplate,
      variant: "outline",
    },
    {
      label: "Archive Template",
      icon: Archive,
      onClick: handleArchiveTemplate,
      variant: "outline",
      className: "text-red-600 border-red-200 hover:bg-red-50",
    },
  ];

  return (
    <div className="space-y-4">
      <h2 className="text-xl font-bold text-gray-900 mb-4">Quick Actions</h2>

      {actions.map((action) => (
        <Button
          key={action.label}
          onClick={action.onClick}
          variant={action.variant}
          className={`w-full justify-start gap-3 ${action.className || ""}`}
          isLoading={action.isLoading}
        >
          <action.icon className="h-4 w-4" />
          {action.label}
        </Button>
      ))}
    </div>
  );
};
